import * as ko from "knockout";

import { TextBoxObservable } from "../forms-knockoutcontrols/pliTextBoxObservable";
import { dirtyFlag, KnockoutDirtyFlag } from "../knockout/util";


/**
 * Unwraps the raw and display value of an item at the given key.
 */
function getValue<TViewModel>(item: TViewModel, key: string): ObservableValue {
    const o = (item as any)[key];
    if (o && typeof o.peekValue === "function") {
        return { raw: o._raw, value: o.peekValue() };
    }
    if (o && typeof o.peek === "function") {
        return { raw: null, value: o.peek() };
    }
    return { raw: null, value: o };
}

/**
 * Returns the error message or "null" after validating a given observable property.
 */
function getValidationError(item: any, key: string): string {
    const o = item[key];
    if (o && typeof o.validate === "function") {
        const isValid = o.validate();
        return isValid ? null : o._errorMessage;
    }
    return null;
}

/**
 * Returns true if the given observable is a TextBoxObservable (i.e., has a 'reset' function).
 */
function isTextBoxObservable(o: ko.Observable<any>): o is TextBoxObservable<any> {
    const oAny = o as any;
    return oAny && typeof oAny.reset === "function";
}

/**
 * Sets the error message for TextBoxObservables if a matching error is present on a record.
 */
function setObservableError(item: any, key: string, record: any): void {
    const o = item[key];
    const e: string = record._errors && record._errors[key];
    if (e && o && typeof o.setErrorMessage === "function") {
        o.setErrorMessage(e);
    }
}

/**
 * Calls 'load' if the given observable is a TextBoxObservable, otherwise uses the normal write function.
 */
function setObservableValue(item: any, key: string, value: any, deferValidate: boolean): void {
    const o: (ko.Observable<any> | TextBoxObservable<any>) = item[key];
    if (isTextBoxObservable(o)) {
        if (deferValidate) {
            // reset to ensure previous validation errors and activated state are cleared
            o.reset();
        }

        o.load(value, /* ignoreFocus */ true); // update view even if currently focused

        if (!deferValidate) {
            o.validate();
        }
    } else if (ko.isWritableObservable(o)) {
        o(value);
    } else {
        item[key] = value;
    }
}

/**
 * Iterates the writeable observables on an item and executes an action on each field.
 * @param item
 *     Item to iterate.
 * @param action
 *     Action to invoke on each pair (key, field observable).
 */
export function forEachWriteableField<TViewModel>(item: TViewModel, action: (key: string, o: ko.Observable<any>) => void): void {
    const keys = Object.keys(item).filter(name => ko.isWriteableObservable((<any>item)[name]));
    for (const key of keys) {
        const o: ko.Observable<any> = (item as any)[key];
        action(key, o);
    }
}

/**
 * For non-observable properties, adds or removes the given error string to an _errors dictionary on the item.
 */
export function setItemError(item: any, key: string, error: string): void {
    // synchronize pliObservable's error
    const targetProperty = item[key];
    if (targetProperty && targetProperty.setErrorMessage) {
        targetProperty.setErrorMessage(error);
    }

    // update "_errors" property
    let errors = item._errors; // cache in local var to avoid prototype chain lookup overhead
    if (error) {
        if (!errors) { errors = item._errors = {}; }
        errors[key] = error;
    } else {
        if (errors) { errors[key] = undefined; }
    }
}

/**
 * Raw/value tuple for a TextBoxObservable instance.
 */
export interface ObservableValue
{
    /**
     * String bound to the input element.
     */
    raw: string;

    /**
     * Formatted/parsed/validated value represented by "raw".
     */
    value: any;
}

/**
 * Base interface for an EditGrid record.
 */
export interface RecordBase {
    /**
     * Will be true if a record is newly created and uninitialized.
     */
    isNewRecord?: boolean;
}

/**
 * Base interface for an EditGrid view model.
 */
export interface ViewModelBase {
    /**
     * Allows applications to override value initialization logic.
     *
     * @param commonValues
     *     Map of property names to either the common values for all items
     *     or "null" if item values were different for that key.
     * @param deferValidate
     *     Will be true if validation was requested to be deferred by
     *     the EditGrid.
     */
    initializeValues?(commonValues: { [key: string]: ObservableValue }, deferValidate: boolean): void;
}

/**
 * Allows efficient batch editing of a set of items.
 */
export class BatchEditWrapper<TViewModel extends ViewModelBase, TRecord extends RecordBase> {
    constructor(items: TRecord[], itemView: TViewModel, readOnly: (() => boolean) | boolean = false, deferValidate = false) {
        this._deferValidate = deferValidate || items.length > 1;
        this._itemView = itemView;
        this._readOnly = typeof readOnly === "function" ? readOnly : () => readOnly;
        this.items = items;

        if (this.items.length > 0) {
            this._initializeValues();
            if (!this._deferValidate) {
                this.updateErrors();
            }
        }

        // create tracking dirty flag; in suspended state if there are no items
        this.dirty = dirtyFlag(this._itemView, this.items.length === 0, false);
    }


    // -------------------------------------------------------------------------
    // Wrapper properties
    // -------------------------------------------------------------------------

    /** True if any writeable properties have been changed; false otherwise. */
    dirty: KnockoutDirtyFlag;

    /** Items currently being edited as a batch. */
    items: TRecord[];


    // -------------------------------------------------------------------------
    // Wrapper actions
    // -------------------------------------------------------------------------

    /**
     * Commits any pending changes to the contained items and resets dirty state.
     */
    commit(): void {
        if (!this._readOnly() && !this.dirty.isSuspended()) {
            const original = JSON.parse(this.dirty.originalJSON());
            forEachWriteableField(this._itemView, (key, o) => {
                const value = o.peek();
                if (value !== original[key]) {
                    const raw = (o as any)._raw;
                    const error = getValidationError(this._itemView, key);
                    for (const item of this.items) {
                        // passing (raw || value) ensures that both valid and
                        // invalid (i.e. unparsable) values are propagated to the
                        // item
                        setObservableValue(item, key, (raw || value), false);

                        // update _errors dictionary for non-observable properties
                        setItemError(item, key, error);

                        // indicate item is no longer "new"
                        if (item.isNewRecord) { item.isNewRecord = false; }
                    }
                }
            });
            this.dirty.reset();
        }
    }

    /**
     * Aborts pending changes and disposes item property subscriptions.
     */
    dispose(): void {
        let forceNotify = false;

        // Validate single-item deferred batches, which are probably "new" items.
        // These are deferred to avoid showing validation errors before the user
        // has entered any data.
        //
        // But since items may be plain records (i.e., fields are not observable),
        // we can only validate based on the values in _itemView. When there are
        // multiple items, conflicting values will be set to "null" which means
        // they cannot be properly validated. This is not so important because
        // multi-item batches will be validated during "commit".
        if (this._deferValidate && this.items.length === 1) {
            forEachWriteableField(this._itemView, key => {
                const item: any = this.items[0];
                const o = item[key];
                if (o && typeof o.validate === "function") {
                    const wasValid = o.isValid.peek();
                    const isValid = o.validate();
                    if (wasValid !== isValid) {
                        // only notify if there was a change in validation state
                        // to avoid unnecessary redraws
                        forceNotify = true;
                    }
                } else {
                    const error = getValidationError(this._itemView, key);
                    setItemError(item, key, error);
                }
            });
        }

        this.dirty.reset();
        this.dirty.suspend();
        if (forceNotify) {
            this.dirty.valueHasMutated();
        }
    }

    /**
     * Rejects pending changes and resets the dirty state.
     */
    reject(): void {
        this._initializeValues();
        this.dirty.reset();
    }

    /**
     * Updates error state for all properties with a common value.
     *
     * If multiple items contain an error for a single property,
     * only the last item's error message will be used.
     */
    updateErrors(): void {
        forEachWriteableField(this._itemView, key => {
            const value = getValue(this._itemView, key).value;
            // if only one item, we can apply the error regardless of the value;
            // otherwise, only apply if items have same value for this key
            if (this.items.length === 1 || value !== null) {
                for (const item of this.items) {
                    setObservableError(this._itemView, key, item);
                }
            }
        });
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Enumerates writeable fields on item view and sets common values for all items.
     *
     * For each view model property, if all items have the same value,
     * that value will be set. If one of the items has a different
     * value, the property will be set to "null".
     *
     * If the item viewmodel defines an "initializeValues" function,
     * that function will be passed the map of keys to common values;
     * otherwise, the properties will be initialized in no particular
     * order.
     */
    private _initializeValues(): void {
        const commonValues: { [key: string]: ObservableValue } = {};

        forEachWriteableField(this._itemView, key => {
            let commonValue: ObservableValue = null;
            for (const item of this.items) {
                if (!commonValue) {
                    commonValue = getValue(item, key);
                } else {
                    if (commonValue.value !== getValue(item, key).value) {
                        commonValue = null;
                        break;
                    }
                }
            }
            commonValues[key] = commonValue;
        });

        if (typeof this._itemView.initializeValues === "function") {
            this._itemView.initializeValues(commonValues, this._deferValidate);
        } else {
            for (const key in commonValues) {
                if (commonValues.hasOwnProperty(key)) {
                    const commonValue = commonValues[key];
                    setObservableValue(this._itemView, key, commonValue && (commonValue.raw || commonValue.value), this._deferValidate);
                }
            }
        }
    }

    /** Whether validation should be deferred when initializing values. */
    private _deferValidate: boolean;

    /** Dummy item matching the shape of the contained items. */
    private _itemView: TViewModel;

    /** Returns true if the current view item should be readonly. */
    private _readOnly: () => boolean;

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/ko-editgrid/shared.ts